import { formatBillAmount, formatDiscountLabelForBill } from "../bill-print-format";
import type {
  LocalOrderCouponSnapshot,
  LocalOrderDiscountBreakdown,
  LocalOrderPricingSource,
  LocalOrderRewardSnapshot,
  LocalOrderTotals,
  LocalOrderVoucherSnapshot,
} from "./types";

export type LocalPricingBaseTotals = {
  subtotal: number;
  taxTotal: number;
  serviceChargeTotal: number;
};

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function buildLocalDiscountBreakdown(input: {
  couponSnapshots?: LocalOrderCouponSnapshot[];
  voucherSnapshots?: LocalOrderVoucherSnapshot[];
  rewardSnapshot?: LocalOrderRewardSnapshot;
}): LocalOrderDiscountBreakdown {
  const items: LocalOrderDiscountBreakdown = [];

  for (const coupon of input.couponSnapshots ?? []) {
    items.push({
      id: `coupon_${coupon.code}_${coupon.applyScope}`,
      source: "coupon",
      code: coupon.code,
      label: `Coupon: ${coupon.code}`,
      amount: coupon.discountAmount,
      scope: coupon.applyScope === "order" ? "order" : "line",
      cartIndices: coupon.cartIndices,
      orderItemIds: coupon.orderItemIds,
    });
  }

  for (const voucher of input.voucherSnapshots ?? []) {
    items.push({
      id: `voucher_${voucher.code}`,
      source: "voucher",
      code: voucher.code,
      label: `Voucher: ${voucher.code}`,
      amount: voucher.appliedAmount,
      scope: "order",
    });
  }

  if (input.rewardSnapshot && input.rewardSnapshot.discountAmount > 0) {
    items.push({
      id: `reward_${input.rewardSnapshot.rewardId}`,
      source: "loyalty_reward",
      label: `Reward: ${input.rewardSnapshot.rewardName}`,
      amount: input.rewardSnapshot.discountAmount,
      scope: "order",
    });
  }

  return items;
}

export function applyCouponSnapshotToLocalTotals(
  base: LocalPricingBaseTotals,
  couponSnapshots: LocalOrderCouponSnapshot[],
): LocalOrderTotals {
  const discountTotal = roundMoney(
    couponSnapshots.reduce((sum, snapshot) => sum + snapshot.discountAmount, 0),
  );

  return {
    subtotal: base.subtotal,
    taxTotal: base.taxTotal,
    serviceChargeTotal: base.serviceChargeTotal,
    discountTotal,
    grandTotal: roundMoney(
      base.subtotal + base.taxTotal + base.serviceChargeTotal - discountTotal,
    ),
  };
}

export function applyRewardSnapshotToLocalTotals(
  totals: LocalOrderTotals,
  rewardSnapshot?: LocalOrderRewardSnapshot,
): LocalOrderTotals {
  if (!rewardSnapshot || rewardSnapshot.discountAmount <= 0) {
    return totals;
  }
  const discountTotal = roundMoney(
    totals.discountTotal + rewardSnapshot.discountAmount,
  );
  return {
    ...totals,
    discountTotal,
    grandTotal: roundMoney(
      totals.subtotal +
        totals.taxTotal +
        totals.serviceChargeTotal -
        discountTotal,
    ),
  };
}

export function applyVoucherSnapshotToLocalTotals(
  totals: LocalOrderTotals,
  voucherSnapshots: LocalOrderVoucherSnapshot[],
): LocalOrderTotals {
  const voucherDiscount = roundMoney(
    voucherSnapshots.reduce((sum, snapshot) => sum + snapshot.appliedAmount, 0),
  );
  const discountTotal = roundMoney(totals.discountTotal + voucherDiscount);

  return {
    ...totals,
    discountTotal,
    grandTotal: roundMoney(
      totals.subtotal +
        totals.taxTotal +
        totals.serviceChargeTotal -
        discountTotal,
    ),
  };
}

export function resolveLocalPricingSource(
  couponSnapshots: LocalOrderCouponSnapshot[],
  voucherSnapshots: LocalOrderVoucherSnapshot[],
  rewardSnapshot?: LocalOrderRewardSnapshot,
): LocalOrderPricingSource | undefined {
  const sources = new Set<"network" | "cache">();
  for (const coupon of couponSnapshots) {
    sources.add(coupon.source);
  }
  for (const voucher of voucherSnapshots) {
    sources.add(voucher.source);
  }
  if (rewardSnapshot) {
    sources.add(rewardSnapshot.source);
  }

  if (sources.size === 0) return undefined;
  if (sources.size === 1) {
    const [only] = sources;
    return only === "network" ? "network" : "cache";
  }
  return "mixed";
}

export function buildLocalPricingSnapshot(input: {
  base: LocalPricingBaseTotals;
  couponSnapshots: LocalOrderCouponSnapshot[];
  voucherSnapshots?: LocalOrderVoucherSnapshot[];
  rewardSnapshot?: LocalOrderRewardSnapshot;
}): {
  couponSnapshots: LocalOrderCouponSnapshot[];
  voucherSnapshots: LocalOrderVoucherSnapshot[];
  rewardSnapshot?: LocalOrderRewardSnapshot;
  discountBreakdown: LocalOrderDiscountBreakdown;
  totals: LocalOrderTotals;
  pricingSource?: LocalOrderPricingSource;
} {
  const voucherSnapshots = input.voucherSnapshots ?? [];
  let totals = applyCouponSnapshotToLocalTotals(
    input.base,
    input.couponSnapshots,
  );
  totals = applyVoucherSnapshotToLocalTotals(totals, voucherSnapshots);
  totals = applyRewardSnapshotToLocalTotals(totals, input.rewardSnapshot);

  return {
    couponSnapshots: input.couponSnapshots,
    voucherSnapshots,
    rewardSnapshot: input.rewardSnapshot,
    discountBreakdown: buildLocalDiscountBreakdown({
      couponSnapshots: input.couponSnapshots,
      voucherSnapshots,
      rewardSnapshot: input.rewardSnapshot,
    }),
    totals,
    pricingSource: resolveLocalPricingSource(
      input.couponSnapshots,
      voucherSnapshots,
      input.rewardSnapshot,
    ),
  };
}

export function mergeLocalPricingSnapshot(input: {
  base: LocalPricingBaseTotals;
  existingCouponSnapshots?: LocalOrderCouponSnapshot[];
  paymentCouponSnapshots?: LocalOrderCouponSnapshot[];
  paymentVoucherSnapshots?: LocalOrderVoucherSnapshot[];
  rewardSnapshot?: LocalOrderRewardSnapshot;
}) {
  const couponSnapshots = [
    ...(input.existingCouponSnapshots ?? []),
    ...(input.paymentCouponSnapshots ?? []),
  ];

  return buildLocalPricingSnapshot({
    base: input.base,
    couponSnapshots,
    voucherSnapshots: input.paymentVoucherSnapshots,
    rewardSnapshot: input.rewardSnapshot,
  });
}

export function buildOrderSummaryCouponSnapshot(input: {
  validatedCoupon: {
    code: string;
    calculatedDiscount: number;
    coupon?: {
      id?: string;
      discountType?: string;
      discountValue?: number | string | null;
    };
  };
  selectedCartIndices: number[];
  cartItems: Array<{ totalPrice: number }>;
  source?: "network" | "cache";
}): LocalOrderCouponSnapshot | undefined {
  if (input.selectedCartIndices.length === 0) return undefined;

  const baseAmount = roundMoney(
    input.selectedCartIndices.reduce(
      (sum, index) => sum + (input.cartItems[index]?.totalPrice ?? 0),
      0,
    ),
  );
  const discountType =
    input.validatedCoupon.coupon?.discountType === "PERCENT" ||
    input.validatedCoupon.coupon?.discountType === "AMOUNT"
      ? input.validatedCoupon.coupon.discountType
      : undefined;

  return {
    code: input.validatedCoupon.code,
    couponId: input.validatedCoupon.coupon?.id,
    discountType,
    discountValue:
      input.validatedCoupon.coupon?.discountValue != null
        ? Number(input.validatedCoupon.coupon.discountValue)
        : undefined,
    discountAmount: input.validatedCoupon.calculatedDiscount,
    applyScope:
      input.selectedCartIndices.length < input.cartItems.length
        ? "cart_indices"
        : "order",
    cartIndices:
      input.selectedCartIndices.length < input.cartItems.length
        ? [...input.selectedCartIndices]
        : undefined,
    baseAmount,
    validatedAt: new Date().toISOString(),
    source: input.source ?? "network",
  };
}

export function buildPaymentCouponSnapshots(input: {
  couponApplyMode: "order" | "per_line";
  validatedCoupon?: {
    code: string;
    calculatedDiscount: number;
    validationSource?: "network" | "cache";
  } | null;
  lineCoupons?: Record<
    string,
    {
      code: string;
      calculatedDiscount: number;
      validationSource?: "network" | "cache";
    }
  >;
  orderItems?: Array<{ id: string; lineSubtotal: number; taxTotal?: number }>;
  baseAmountOrderCoupon: number;
  source?: "network" | "cache";
}): LocalOrderCouponSnapshot[] {
  const validatedAt = new Date().toISOString();
  const defaultSource = input.source ?? "network";
  const snapshots: LocalOrderCouponSnapshot[] = [];

  if (input.couponApplyMode === "order" && input.validatedCoupon) {
    snapshots.push({
      code: input.validatedCoupon.code,
      discountAmount: input.validatedCoupon.calculatedDiscount,
      applyScope: "order",
      baseAmount: input.baseAmountOrderCoupon,
      validatedAt,
      source: input.validatedCoupon.validationSource ?? defaultSource,
    });
    return snapshots;
  }

  if (input.couponApplyMode === "per_line" && input.orderItems) {
    for (const item of input.orderItems) {
      const lineCoupon = input.lineCoupons?.[item.id];
      if (!lineCoupon) continue;
      snapshots.push({
        code: lineCoupon.code,
        discountAmount: lineCoupon.calculatedDiscount,
        applyScope: "order_items",
        orderItemIds: [item.id],
        baseAmount: roundMoney(item.lineSubtotal + (item.taxTotal ?? 0)),
        validatedAt,
        source: lineCoupon.validationSource ?? defaultSource,
      });
    }
  }

  return snapshots;
}

export function buildPaymentRewardSnapshot(
  snapshot: LocalOrderRewardSnapshot | null | undefined,
): LocalOrderRewardSnapshot | undefined {
  if (!snapshot || snapshot.discountAmount <= 0) return undefined;
  return snapshot;
}

export function buildPaymentVoucherSnapshots(
  appliedVouchers: Array<{ code: string; value: number }>,
  source: "network" | "cache" = "network",
): LocalOrderVoucherSnapshot[] {
  const validatedAt = new Date().toISOString();
  return appliedVouchers.map((voucher) => ({
    code: voucher.code,
    value: voucher.value,
    appliedAmount: voucher.value,
    validatedAt,
    source,
  }));
}

export function buildLocalDiscountsHtml(
  order: Pick<
    import("./types").LocalOrderRecord,
    "discountBreakdown" | "totals"
  >,
): string {
  const breakdown = order.discountBreakdown ?? [];
  if (breakdown.length > 0) {
    return breakdown
      .map(
        (entry) =>
          `<tr><td class="summary-label">${formatDiscountLabelForBill(entry.label)}</td><td class="summary-value discount-value">-${formatBillAmount(entry.amount)} ₭</td></tr>`,
      )
      .join("");
  }

  if (order.totals.discountTotal > 0) {
    return `<tr><td class="summary-label">Discount</td><td class="summary-value discount-value">-${formatBillAmount(order.totals.discountTotal)} ₭</td></tr>`;
  }

  return "";
}
